/**
 * plugins/vuetify.ts
 *
 * Framework documentation: https://vuetifyjs.com`
 */

// Styles
// import '@mdi/font/css/materialdesignicons.css';
import 'vuetify/styles';
import colors from 'vuetify/util/colors';

// Composables
import { createVuetify } from 'vuetify';
import { Theme } from '@/constants';
import { mdi, aliases } from 'vuetify/iconsets/mdi-svg';
import SoccerBall from '@/pages/core/icon/ball/SoccerBall.vue';
import CornerFlag from '@/pages/core/icon/corner/CornerFlag.vue';

// const isDark = localStorage.hasOwnProperty('isDark') ? JSON.parse(<string>localStorage.getItem('isDark')) : false;

// https://vuetifyjs.com/en/introduction/why-vuetify/#feature-guides
export default createVuetify({
    defaults: {
        global: {
            density: 'compact',
            flat: true,
        },
        VTextField: {
            clearIcon: mdiClose, //global replace mdiCloseCircle to mdiClose
        },
        VCombobox: {
            clearIcon: mdiClose,
        },
        VSelect: {
            clearIcon: mdiClose,
        },
        VAutocomplete: {
            clearIcon: mdiClose,
        },
        VTooltip: {
            location: 'bottom',
            openDelay: 500,
            openOnFocus: false,
            disabled: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
        },
        VDatePicker: {
            VBtn: {
                density: 'default',
            },
        },
        VAppBar: {
            density: 'default',
        },
        VBtn: {
            color: 'primary',
        },
        VBtnToggle: {
            density: 'default',
        },
        VSwitch: {
            flat: false,
        },
        VCheckboxBtn: {
            color: 'rgba(0, 0, 0, 0.54)',
            baseColor: 'rgba(0, 0, 0, 0.54)',
            style: 'font-size: 14px;',
        },
    },
    icons: {
        defaultSet: 'mdi',
        aliases: {
            ...aliases,
            soccerBall: SoccerBall,
            cornerFlag: CornerFlag,
        },
        sets: { mdi },
    },
    theme: {
        defaultTheme: Theme.Light,
        themes: {
            light: {
                colors: {
                    primary: '#7a7a7a',
                    accent: '#7a7a7a',
                    tbGreen: '#6aa84f',
                    tbGreenIcon: '#6aa84f',
                    tbGreenBadge: '#6aa84f',
                    lightGreen: '#4caf50',
                    lightRed: '#fc0d1b',
                    lightBlack: '#212121',
                    lightGrey: '#757575',
                    greyLighten4: '#f5f5f5',
                    red: '#f44336',
                    cancelOrderBtn: '#f44336',
                    cancelOrderBtnDisabled: '#f4b4b4',
                    white: '#ffffff',
                    message: '#1867c0',
                    background: '#ffffff',
                    error: '#ff5252',
                    surface: '#ffffff',
                    chipLiveBg: '#fff0dd',
                    chipLiveText: '#ff9900',
                    chipDballBg: '#c5c5c5',
                    chipDballText: '#FFFFFF',
                    favStarOn: colors.orange.lighten1,
                    favStarOff: colors.grey.lighten1,
                    // custom tokens for global.scss
                    tbRed: '#fe0000',
                    tbBlue: '#4378bb',
                    scrollThumb: '#d3d3d3',
                    scrollTrack: '#ffffff',
                    // neutrals & text
                    gray500: '#9e9e9e',
                    gray600: '#666666',
                    gray650: '#545454',
                    darkText: '#000000',
                    darkTextSecondary: '#666666',
                    lightText: '#ffffff',
                    lightTextSecondary: '#f0f0f0',
                    // borders & dividers
                    borderLightThin: '#e0e0e0',
                    borderLight: '#efefef',
                    borderLightAlt: '#e6e6e6',
                    // table & hovers
                    hoverRowLight: '#fcfbfb',
                    totalRowBg: '#f7f7f7',
                    headerBg: '#1a1d25',
                    // tooltips
                    tooltipTextLight: '#ffffff',
                    tooltipBgLight: '#424242',
                    tooltipBorderLight: '#424242',
                    // cards & titles
                    cardTitleLight: '#666666',
                    // feed & ft
                    timeOrange: '#ff9900',
                    ftText: '#545454',
                    // statuses
                    statusAbn: '#f1c232',
                    statusRunning: '#38761d',
                    // highlights & badges
                    highlightYellow: '#faf9a0',
                    highlightYellowAlt: '#ffe990',
                    greenBgLight: '#bdffce',
                    redBgLight: '#f7b7b7',
                    // text specials
                    darkTextMuted: '#808080',
                    purpleText: '#c49ae5',
                    brownText: '#b3772a',
                    infoBlue: '#6fa8dc',
                    successGreen: '#93c47d',
                    neutralGray: '#808080',
                    paleText: '#a7aab4',
                    // misc backgrounds
                    dividerGrey: '#dedede',
                    darkPanelBg: '#2f3032',
                    darkOverlayBg: '#1e1e1e',
                    neutral700: '#7e7e7e',
                    // toast backgrounds
                    toastGreen: '#008000',
                    toastLightGreen: '#4caf50',
                    toastLightRed: '#fc0d1b',
                    toastLightOrange: '#ff9900',
                    // bookmaker brand colors (light theme)
                    bookmaker10: '#ff5500',
                    bookmaker1: '#002e6c',
                    bookmaker3: '#5372ac',
                    bookmaker11: '#005289',
                    bookmaker5: '#38761d',
                    bookmaker17: '#f5b942',
                    bookmaker19: '#3a343f',
                    bookmaker21: '#4da1a9',
                    bookmaker22: '#54292e',
                    bookmaker23: '#642775',
                    gridStatsChip: '#C5C5C5',
                    gridStatsPositionRedChip: '#FFCDD2',
                    gridStatsPositionGreenChip: '#6aa84f',
                },
            },
            dark: {
                colors: {
                    primary: '#1e1f22',
                    accent: '#7a7a7a',
                    tbGreen: '#6aa84f',
                    tbGreenIcon: '#b5bac1',
                    tbGreenBadge: '#3c5e2c',
                    lightGreen: '#4caf50',
                    lightRed: '#fc0d1b',
                    lightBlack: '#212121',
                    lightGrey: '#757575',
                    greyLighten4: '#f5f5f5',
                    red: '#f6475d',
                    cancelOrderBtn: '#f6475d',
                    cancelOrderBtnDisabled: '#3A3A3A',
                    white: '#f0f0f0',
                    message: '#1867c0',
                    background: '#1e1f22',
                    error: '#ff5252',
                    surface: '#1e1f22',
                    chipLiveBg: '#323338',
                    chipLiveText: '#ff9900',
                    chipDballBg: '#323338',
                    chipDballText: '#f0f0f0',
                    favStarOn: colors.orange.lighten1,
                    favStarOff: colors.grey.lighten1,
                    // custom tokens for global.scss
                    tbRed: '#f6475d',
                    tbBlue: '#20b4fe',
                    scrollThumb: '#323338',
                    scrollTrack: '#1e1f22',
                    // neutrals & text
                    gray500: '#9e9e9e',
                    gray600: '#666666',
                    gray650: '#545454',
                    darkText: '#b5bac1',
                    darkTextSecondary: '#b5bac1',
                    lightText: '#f0f0f0',
                    lightTextSecondary: '#f0f0f0',
                    // borders & dividers
                    borderDarkThin: '#404148',
                    borderDarkThinAlt: '#4c4b50',
                    borderLight: '#efefef',
                    // table & hovers
                    hoverRowDark: '#2b2c30',
                    totalRowBg: '#323338',
                    headerBg: '#1a1d25',
                    // tooltips
                    tooltipTextDark: '#f0f0f0',
                    tooltipBgDark: '#2c2d31',
                    tooltipBorderDark: '#3a3b40',
                    // cards & titles
                    cardTitleDark: '#f0f0f0',
                    // feed & ft
                    timeOrange: '#ff9900',
                    ftText: '#b5bac1',
                    // statuses
                    statusAbn: '#f1c232',
                    statusRunning: '#38761d',
                    // highlights & badges (use same colors to keep contrast)
                    highlightYellow: '#faf9a0',
                    highlightYellowAlt: '#ffe990',
                    greenBgLight: '#bdffce',
                    redBgLight: '#f7b7b7',
                    // text specials
                    darkTextMuted: '#808080',
                    purpleText: '#c49ae5',
                    brownText: '#b3772a',
                    infoBlue: '#6fa8dc',
                    successGreen: '#93c47d',
                    neutralGray: '#808080',
                    paleText: '#a7aab4',
                    // misc backgrounds
                    dividerGrey: '#dedede',
                    darkPanelBg: '#2f3032',
                    darkOverlayBg: '#1e1e1e',
                    neutral700: '#7e7e7e',
                    // toast backgrounds (dark behaves same visual overlays)
                    toastGreen: '#008000',
                    toastLightGreen: '#4caf50',
                    toastLightRed: '#fc0d1b',
                    toastLightOrange: '#ff9900',
                    // bookmaker brand colors (dark theme fallback to neutral when not specified)
                    bookmaker10: '#b5bac1',
                    bookmaker1: '#b5bac1',
                    bookmaker3: '#b5bac1',
                    bookmaker11: '#b5bac1',
                    bookmaker5: '#b5bac1',
                    bookmaker17: '#b5bac1',
                    bookmaker19: '#b5bac1',
                    bookmaker21: '#b5bac1',
                    bookmaker22: '#b5bac1',
                    bookmaker23: '#642775',
                    gridStatsChip: '#b5bac1',
                    gridStatsPositionRedChip: '#5c1919',
                    gridStatsPositionGreenChip: '#3c5e2c',
                },
            },
        },
    },
});
